import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import toast from 'react-hot-toast';
import { AppDialog } from '../../../../components/common/CustomMdal';
import useDebounce from '../../../../hooks/useDebounce';
import { QUERY_KEY } from '../../../../services/queryEndPoint';
import { Images } from '../../../../utils/imageConstants';
import { mergeDuplicatePlayers, previewPlayerMerge } from '../api/playersApi';
import type {
  IDuplicatePlayerCandidate,
  IDuplicatePlayerGroup,
  PlayerDuplicateConfidence,
} from '../types/players';

interface DuplicatePlayersReviewModalProps {
  leagueId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groups: IDuplicatePlayerGroup[];
  onMergeComplete: () => void;
}

interface GroupSelection {
  keepPlayerId: string;
  includedIds: Set<string>;
}

const GROUP_COLORS = ['#7C4DFF', '#F2994A', '#201E52', '#8E2C4B', '#1AA915', '#0F9BB0'];

function colorForIndex(index: number): string {
  return GROUP_COLORS[index % GROUP_COLORS.length];
}

function getInitials(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '-';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function formatShortDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

interface SameMatchConflict {
  playerNames: [string, string];
  matchIds: string[];
}

const SAME_MATCH_CONFLICT_PATTERN =
  /^Players ([0-9a-fA-F-]{36}) and ([0-9a-fA-F-]{36}) both have an association with match\(es\) \[([^\]]*)\]/;

/** Parses LeagueService.mergeDuplicatePlayers' same-match guard message into a
 * friendlier shape. Returns null for any other error — those still fall back
 * to a generic toast. */
function parseSameMatchConflict(
  message: string | undefined,
  profiles: IDuplicatePlayerCandidate[]
): SameMatchConflict | null {
  if (!message) return null;
  const match = message.match(SAME_MATCH_CONFLICT_PATTERN);
  if (!match) return null;
  const [, playerIdA, playerIdB, matchIdsRaw] = match;
  const matchIds = matchIdsRaw
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
  if (matchIds.length === 0) return null;
  const nameFor = (playerId: string) =>
    profiles.find((p) => p.playerId === playerId)?.fullName || 'One of these players';
  return { playerNames: [nameFor(playerIdA), nameFor(playerIdB)], matchIds };
}

function confidenceLabel(confidence: PlayerDuplicateConfidence): string {
  switch (confidence) {
    case 'STRONG_MATCH':
      return 'Strong match';
    case 'LIKELY_MATCH':
      return 'Likely match';
    case 'NEEDS_A_LOOK':
    default:
      return 'Needs a look';
  }
}

/** Matches a group if the query hits any profile's name (first, last, or "first
 * last" — all covered by a substring check against `fullName`) or player code. */
function groupMatchesQuery(group: IDuplicatePlayerGroup, normalizedQuery: string): boolean {
  if (!normalizedQuery) return true;
  if ((group.displayName ?? '').toLowerCase().includes(normalizedQuery)) return true;
  return group.profiles.some((profile) => {
    const fullName = (profile.fullName ?? '').toLowerCase();
    const code = (profile.playerCode ?? '').toLowerCase();
    return fullName.includes(normalizedQuery) || code.includes(normalizedQuery);
  });
}

function initialSelection(group: IDuplicatePlayerGroup): GroupSelection {
  const keep = group.profiles.find((p) => p.suggestedKeep) ?? group.profiles[0];
  const includedIds = new Set(
    group.profiles
      .filter((p) => p.suggestedInclude && p.playerId !== keep?.playerId)
      .map((p) => p.playerId)
  );
  return { keepPlayerId: keep?.playerId ?? '', includedIds };
}

const DuplicatePlayersReviewModal: React.FC<DuplicatePlayersReviewModalProps> = ({
  leagueId,
  open,
  onOpenChange,
  groups,
  onMergeComplete,
}) => {
  const queryClient = useQueryClient();
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [selectionByGroup, setSelectionByGroup] = useState<Record<string, GroupSelection>>({});
  const [mergeConflict, setMergeConflict] = useState<SameMatchConflict | null>(null);
  const [sidebarSearch, setSidebarSearch] = useState('');

  // Fresh search box each time the modal is opened, rather than carrying over
  // whatever was typed the last time it was reviewed.
  useEffect(() => {
    if (!open) setSidebarSearch('');
  }, [open]);

  const normalizedSidebarSearch = sidebarSearch.trim().toLowerCase();
  const visibleGroups = useMemo(
    () => groups.filter((group) => groupMatchesQuery(group, normalizedSidebarSearch)),
    [groups, normalizedSidebarSearch]
  );

  // Default to the first group, and fall back to it if the selected one disappears
  // (e.g. it was just merged away and the groups list refetched without it).
  useEffect(() => {
    if (groups.length === 0) {
      setSelectedGroupId(null);
      return;
    }
    if (!selectedGroupId || !groups.some((g) => g.groupId === selectedGroupId)) {
      setSelectedGroupId(groups[0].groupId);
    }
  }, [groups, selectedGroupId]);

  const selectedGroup = groups.find((g) => g.groupId === selectedGroupId) ?? null;
  const selectedGroupIndex = groups.findIndex((g) => g.groupId === selectedGroupId);

  const selection: GroupSelection | null = selectedGroup
    ? selectionByGroup[selectedGroup.groupId] ?? initialSelection(selectedGroup)
    : null;

  const setSelection = (groupId: string, next: GroupSelection) => {
    setSelectionByGroup((prev) => ({ ...prev, [groupId]: next }));
  };

  const setKeep = (playerId: string) => {
    if (!selectedGroup || !selection) return;
    const nextIncluded = new Set(selection.includedIds);
    nextIncluded.delete(playerId);
    if (selection.keepPlayerId && selection.keepPlayerId !== playerId) {
      nextIncluded.add(selection.keepPlayerId);
    }
    setSelection(selectedGroup.groupId, { keepPlayerId: playerId, includedIds: nextIncluded });
  };

  const toggleIncluded = (playerId: string, included: boolean) => {
    if (!selectedGroup || !selection) return;
    const nextIncluded = new Set(selection.includedIds);
    if (included) nextIncluded.add(playerId);
    else nextIncluded.delete(playerId);
    setSelection(selectedGroup.groupId, { ...selection, includedIds: nextIncluded });
  };

  // Live-updating preview: debounce the selection so rapid clicks don't spam the API,
  // and only fetch once the debounced key matches the live one (avoids firing a preview
  // request for a selection state the user has already moved past).
  const selectionKey = selection
    ? `${selection.keepPlayerId}|${Array.from(selection.includedIds).sort().join(',')}`
    : '';
  const debouncedSelectionKey = useDebounce(selectionKey, 300);

  // A stale conflict banner from a previous group/selection would be misleading, so clear it
  // whenever the user switches groups or changes who's included in the merge.
  useEffect(() => {
    setMergeConflict(null);
  }, [selectedGroupId, selectionKey]);

  const {
    data: preview,
    isFetching: isPreviewLoading,
  } = useQuery({
    queryKey: ['duplicatePlayerPreview', leagueId, selectedGroupId, debouncedSelectionKey],
    enabled: !!selection && selection.includedIds.size > 0 && selectionKey === debouncedSelectionKey,
    queryFn: () =>
      previewPlayerMerge({
        leagueId,
        keepPlayerId: selection!.keepPlayerId,
        mergePlayerIds: Array.from(selection!.includedIds),
      }),
  });

  const mergeMutation = useMutation({
    mutationFn: () =>
      mergeDuplicatePlayers({
        leagueId,
        keepPlayerId: selection!.keepPlayerId,
        mergePlayerIds: Array.from(selection!.includedIds),
      }),
    onMutate: () => {
      setMergeConflict(null);
    },
    onSuccess: async () => {
      toast.success('Players merged successfully.');
      if (selectedGroup) {
        setSelectionByGroup((prev) => {
          const next = { ...prev };
          delete next[selectedGroup.groupId];
          return next;
        });
      }
      setSelectedGroupId(null);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY.leaguePlayersSearch, leagueId] });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY.leagueDuplicatePlayerGroups, leagueId] });
      onMergeComplete();
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      const backendMessage = error.response?.data?.message;
      const conflict = parseSameMatchConflict(backendMessage, selectedGroup?.profiles ?? []);
      if (conflict) {
        setMergeConflict(conflict);
        return;
      }
      toast.error(backendMessage || 'Could not merge these players. Please try again.');
    },
  });

  const mergeCount = selection ? selection.includedIds.size + 1 : 0;

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Review duplicate players"
      description="CricCenter matched these on phone number, date of birth. Nothing is merged until you confirm."
      size={1200}
      className="dup-review-dialog"
      footer={
        selectedGroup && selection ? (
          <button
            type="button"
            className="dup-review-merge-btn"
            disabled={selection.includedIds.size === 0 || mergeMutation.isPending}
            onClick={() => mergeMutation.mutate()}
          >
            {mergeMutation.isPending ? 'Merging…' : `Merge ${mergeCount} profiles`}
          </button>
        ) : undefined
      }
    >
      <div className="dup-review">
        <div className="dup-review-sidebar">
          {groups.length > 0 && (
            <div className="dup-review-search">
              <img src={Images.dark_search_icon} alt="" className="dup-review-search__icon" />
              <input
                type="text"
                className="dup-review-search__input"
                placeholder="Search name or player code"
                value={sidebarSearch}
                onChange={(e) => setSidebarSearch(e.target.value)}
                aria-label="Search duplicate player groups by name or player code"
              />
              {sidebarSearch && (
                <button
                  type="button"
                  className="dup-review-search__clear"
                  onClick={() => setSidebarSearch('')}
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
            </div>
          )}

          {visibleGroups.map((group) => {
            // Colour is keyed to the group's position in the full, unfiltered list so
            // it stays stable while the user types a search query.
            const colorIndex = groups.findIndex((g) => g.groupId === group.groupId);
            return (
              <button
                key={group.groupId}
                type="button"
                className={`dup-review-sidebar-item ${group.groupId === selectedGroupId ? 'active' : ''}`}
                onClick={() => setSelectedGroupId(group.groupId)}
              >
                <span className="dup-review-avatar" style={{ background: colorForIndex(colorIndex) }}>
                  {getInitials(group.displayName)}
                </span>
                <span className="dup-review-sidebar-item__text">
                  <span className="dup-review-sidebar-item__name">{group.displayName ?? 'Duplicate group'}</span>
                  <span className="dup-review-sidebar-item__count">{group.profileCount} profiles</span>
                </span>
                <span className={`dup-review-confidence dup-review-confidence--${group.confidence.toLowerCase()}`}>
                  {confidenceLabel(group.confidence)}
                </span>
              </button>
            );
          })}
          {groups.length === 0 && <div className="dup-review-sidebar-empty">No duplicates found.</div>}
          {groups.length > 0 && visibleGroups.length === 0 && (
            <div className="dup-review-sidebar-empty">
              No players match “{sidebarSearch.trim()}”.
            </div>
          )}
        </div>

        <div className="dup-review-detail">
          {!selectedGroup || !selection ? (
            <div className="dup-review-detail-empty">Select a group on the left to review.</div>
          ) : (
            <>
              <div className="dup-review-detail__header">
                {selectedGroup.profileCount} profiles for {selectedGroup.displayName}
              </div>
              <div className="dup-review-detail__reasons">
                <strong>{confidenceLabel(selectedGroup.confidence)}.</strong>{' '}
                Matched on: {selectedGroup.matchedOnReasons.join(' • ')}
              </div>

              {mergeConflict && (
                <div className="dup-review-conflict">
                  <div className="dup-review-conflict__message">
                    <strong>{mergeConflict.playerNames[0]}</strong> and{' '}
                    <strong>{mergeConflict.playerNames[1]}</strong> both played in the same
                    match{mergeConflict.matchIds.length > 1 ? 'es' : ''} below, so they can't be
                    duplicates of each other. Uncheck one of them, or open the match to check the
                    scorecard.
                  </div>
                  <div className="dup-review-conflict__links">
                    {mergeConflict.matchIds.map((matchId, index) => (
                      <a
                        key={matchId}
                        href={`/matches-details/${matchId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="dup-review-conflict__link"
                      >
                        View match {mergeConflict.matchIds.length > 1 ? index + 1 : ''}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <div className="dup-review-table-wrap">
                <table className="dup-review-table">
                  <thead>
                    <tr>
                      <th>Keep</th>
                      <th>Player Name</th>
                      <th>Tournaments</th>
                      <th>Matches Played</th>
                      <th>Contact</th>
                      <th>Created</th>
                      <th>Include</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedGroup.profiles.map((profile) => {
                      const isKeep = selection.keepPlayerId === profile.playerId;
                      const isIncluded = isKeep || selection.includedIds.has(profile.playerId);
                      const dob = formatShortDate(profile.dob);
                      const created = formatShortDate(profile.createdAt);
                      return (
                        <tr key={profile.playerId}>
                          <td>
                            <input
                              type="radio"
                              name={`dup-review-keep-${selectedGroup.groupId}`}
                              checked={isKeep}
                              onChange={() => setKeep(profile.playerId)}
                            />
                          </td>
                          <td>
                            <div className="dup-review-player">
                              <span
                                className="dup-review-avatar dup-review-avatar--sm"
                                style={{ background: colorForIndex(selectedGroupIndex) }}
                              >
                                {getInitials(profile.fullName)}
                              </span>
                              <div className="dup-review-player__text">
                                <div className="dup-review-player__name">{profile.fullName ?? '—'}</div>
                                {profile.playerCode && (
                                  <div className="dup-review-player__code">{profile.playerCode}</div>
                                )}
                                {profile.currentTeamName && (
                                  <div className="dup-review-player__team">{profile.currentTeamName}</div>
                                )}
                                {isKeep && (
                                  <span className="dup-review-keeping-badge">KEEPING</span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td>
                            {(profile.tournamentNames ?? []).map((name) => (
                              <div key={name} className="dup-review-tournament-line">
                                • {name}
                              </div>
                            ))}
                          </td>
                          <td>{profile.matchesPlayed}</td>
                          <td>
                            <div>
                              {profile.phoneNo ? `+${profile.countryCode ?? ''} ${profile.phoneNo}` : '—'}
                            </div>
                            <div className="dup-review-dob">DOB {dob ?? '—'}</div>
                          </td>
                          <td>{created ?? '—'}</td>
                          <td>
                            <input
                              type="checkbox"
                              checked={isIncluded}
                              disabled={isKeep}
                              onChange={(e) => toggleIncluded(profile.playerId, e.target.checked)}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {isPreviewLoading && <div className="dup-review-preview-loading">Updating preview…</div>}

              {preview && (
                <div className="dup-review-after-merging">
                  <div className="dup-review-after-merging__label">After merging</div>
                  <div className="dup-review-after-merging__identity">
                    <span
                      className="dup-review-avatar"
                      style={{ background: colorForIndex(selectedGroupIndex) }}
                    >
                      {getInitials(preview.resultingIdentity.fullName)}
                    </span>
                    <div>
                      <div className="dup-review-after-merging__name">
                        {preview.resultingIdentity.fullName}
                      </div>
                      <div className="dup-review-after-merging__meta">
                        {[
                          preview.resultingIdentity.teamName,
                          preview.resultingIdentity.phoneNo
                            ? `+${preview.resultingIdentity.countryCode ?? ''} ${preview.resultingIdentity.phoneNo}`
                            : null,
                          preview.resultingIdentity.playerCode,
                        ]
                          .filter(Boolean)
                          .join(' • ')}
                      </div>
                    </div>
                  </div>

                  <table className="dup-review-after-merging__table">
                    <thead>
                      <tr>
                        <th>Tournament</th>
                        <th>Matches</th>
                        <th>Runs</th>
                        <th>Wickets</th>
                        <th>Comes From</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* The backend already appends its own "Career total" row as the
                          last entry (PlayerDuplicateDetectionService.buildMergePreview) —
                          render rows as-is rather than re-summing them here, or the total
                          row's own values get folded into a second, doubled total. */}
                      {preview.rows.map((row, index) => {
                        const isTotal = row.tournamentName === 'Career total';
                        return (
                          <tr
                            key={row.tournamentId ?? `total-${index}`}
                            className={isTotal ? 'dup-review-after-merging__total' : undefined}
                          >
                            <td>{row.tournamentName}</td>
                            <td>{row.matches}</td>
                            <td>{row.runs}</td>
                            <td>{row.wickets}</td>
                            <td>{row.comesFromPlayerCodes.join(', ')}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </AppDialog>
  );
};

export default DuplicatePlayersReviewModal;
